import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Table, Spinner } from "reactstrap";
import { FaBell } from "react-icons/fa";
import io from "socket.io-client";
import axios from "axios";

const NotificationBell = ({ userData }) => {
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const [socket, setSocket] = useState(null);

  // --- Modal & Transaction States ---
  const [modalOpen, setModalOpen] = useState(false);
  const [matchedTransaction, setMatchedTransaction] = useState(null);
  const [returnSpinner, setReturnSpinner] = useState(false);

  // Socket connection setup
  useEffect(() => {
    if (!userData?.branch) return;

    const newSocket = io(process.env.REACT_APP_COLLEGE_ERP_API, {
      query: { branch: userData.branch },
    });

    if (userData?.rights) {
      newSocket.emit("joinBranch", {
        branch: userData.branch,
        role: userData.rights,
        senderId: userData.userId,
      });
    }

    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, [userData]);

  // Listen for real-time socket updates
  useEffect(() => {
    if (!socket || userData?.rights !== "Admin") return;

    const saved = localStorage.getItem("notifications");
    if (saved) setNotifications(JSON.parse(saved));

    const handleAlert = (msg) => {
      setNotifications((prev) => {
        const updated = [{ id: Date.now(), data: msg, read: false }, ...prev];
        localStorage.setItem("notifications", JSON.stringify(updated));
        return updated;
      });
    };

    socket.on("deliveryAlert", handleAlert);
    return () => socket.off("deliveryAlert", handleAlert);
  }, [socket, userData]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !bellRef.current.contains(e.target)
      ) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch full transaction data using the Transaction_ID as the search query
  const fetchTransactionDetails = async (transactionId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_COLLEGE_ERP_API}library_transaction`,
        {
          params: {
            branch: userData.branch,
            page: 1,
            limit: 5,
            search: transactionId, // Using TxID to lookup the precise record
          },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      // Handle both array responses or direct object responses securely
      const transactionData = Array.isArray(res.data) ? res.data[0] : res.data?.data?.[0] || res.data;
      
      if (transactionData) {
        setMatchedTransaction(transactionData);
        setModalOpen(true);
      } else {
        alert("Transaction record could not be found.");
      }
    } catch (err) {
      console.error("❌ Failed to fetch transaction data:", err);
      alert("Error gathering transaction details from the server.");
    }
  };

  const handleNotificationClick = (note) => {
    // Mark clicked item as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === note.id ? { ...n, read: true } : n))
    );

    if (note.data?.Transaction_ID) {
      fetchTransactionDetails(note.data.Transaction_ID);
    } else {
      alert("No Transaction ID linked to this notification.");
    }
  };

  // Process item return
  const handleReturn = async (book, txId) => {
    const confirmReturn = window.confirm(
      `Are you sure you want to initiate return for "${book.Book_Title}"?`
    );
    if (!confirmReturn) return;

    setReturnSpinner(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_COLLEGE_ERP_API}library_book_status`,
        { Availability: "Pending" },
        {
          params: {
            branch: userData.branch,
            transactionId: txId,
            id: book._id,
          },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      // Re-fetch transaction data to refresh the UI state immediately
      await fetchTransactionDetails(txId);
    } catch (err) {
      console.error("❌ Failed to update return:", err);
    } finally {
      setReturnSpinner(false);
    }
  };

  const handleClearAll = () => {
    setNotifications([]);
    localStorage.removeItem("notifications");
  };

  if (userData?.rights !== "Admin") return null;

  return (
    <div style={{ position: "fixed", top: "1rem", right: "1.5rem", zIndex: 9999 }}>
      {/* 🔔 Bell Button */}
      <Button
        innerRef={bellRef}
        color="link"
        onClick={() => setShowPanel((prev) => !prev)}
        style={{ position: "relative", padding: 0, border: "none", background: "transparent", fontSize: "1.8rem", cursor: "pointer" }}
      >
        <FaBell color="#f0ad4e" />
        {notifications.some((n) => !n.read) && (
          <Badge color="danger" pill style={{ position: "absolute", top: "0", right: "0", transform: "translate(50%, -50%)", fontSize: "0.6rem" }}>
            {notifications.filter((n) => !n.read).length}
          </Badge>
        )}
      </Button>

      {/* 📋 Notification Dropdown Panel */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ position: "absolute", top: "2.5rem", right: 0, background: "#fff", borderRadius: "0.5rem", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", width: "320px", maxHeight: "350px", overflowY: "auto", padding: "0.5rem" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Delivery Alerts</strong>
              {notifications.length > 0 && (
                <Button color="link" size="sm" onClick={handleClearAll} style={{ padding: "0", fontSize: "0.8rem" }}>
                  Clear All
                </Button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="text-center text-muted p-2">No new notifications</div>
            ) : (
              notifications.map((note) => (
                <div
                  key={note.id}
                  style={{ background: note.read ? "#f9f9f9" : "#e8f4ff", borderRadius: "0.3rem", padding: "0.5rem", marginBottom: "0.3rem", cursor: "pointer" }}
                  onClick={() => handleNotificationClick(note)}
                >
                  <div>
                    <strong>{note.data.Member_Name}</strong> — {note.data.type}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#333" }}>
                    TxID: {note.data.Transaction_ID}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#777" }}>
                    {new Date(note.data.Timestamp).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 📋 Contextual Transaction Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          Transaction Context Details
        </ModalHeader>
        <ModalBody>
          {matchedTransaction && (
            <>
              {/* Member Data extracted from transaction schema properties */}
              <Table bordered size="sm">
                <tbody>
                  <tr><th>Transaction ID</th><td>{matchedTransaction.Transaction_ID}</td></tr>
                  <tr><th>Member ID</th><td>{matchedTransaction.Member_ID}</td></tr>
                  <tr><th>Member Name</th><td>{matchedTransaction.Member_Name || "N/A"}</td></tr>
                </tbody>
              </Table>

              {/* Books belonging directly to this specific transaction match */}
              {matchedTransaction.Books?.length > 0 && (
                <Table responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Book Number</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchedTransaction.Books.map((book, idx) => (
                      <tr key={book._id || idx}>
                        <td>{idx + 1}</td>
                        <td>{book.Book_Title}</td>
                        <td>{book.Book_Number}</td>
                        <td>
                          <strong className={book.Availability === "Issued" ? "text-danger" : "text-success"}>
                            {book.Availability}
                          </strong>
                          {book.Availability === "Issued" && (
                            returnSpinner ? <Spinner size="sm" className="ms-2" /> : (
                              <button 
                                className="btn btn-sm btn-warning ms-2" 
                                onClick={() => handleReturn(book, matchedTransaction.Transaction_ID)}
                              >
                                Initiate Return
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NotificationBell;